import type { AxisScale } from '@visx/xychart';

import type { ChartDataPoint, XScaleType } from '../../types';
import { formatDateShort } from '../../utils/formatters';
import { extractDomainBounds, getUniqueXValues } from '../../utils/scaleHelpers';

import {
  deduplicateAdjacentTickLabels,
  estimateMaxTicks,
  findMaxLabelLength,
  selectNonOverlappingTicks,
} from './tickCalculation';

/**
 * Type for a function that converts scale values to pixel positions.
 */
export type ScaleFunction = (value: string | number) => number | undefined;

/**
 * Type for a function that formats tick values for display.
 */
export type TickFormatter = (value: string | number) => string;

export function toTimeTickNumber(value: unknown): number {
  if (value instanceof Date) {
    return value.getTime();
  }
  return typeof value === 'string' || typeof value === 'number' ? Number(value) : Number.NaN;
}

/**
 * Determines if the X-axis line should be hidden based on Y-axis positioning.
 * Hides the axis line when X-axis is NOT at y=0 (i.e., when 0 is outside Y-axis domain).
 *
 * @param yScale - The Y-axis scale from visx DataContext
 * @returns true if axis line should be hidden, false otherwise
 */
export function shouldHideXAxisLine(yScale: AxisScale | undefined): boolean {
  if (!yScale) {
    return false; // Default to showing the line if we can't determine position
  }

  if ('domain' in yScale && typeof yScale.domain === 'function') {
    const yDomain = yScale.domain() as [number, number];

    // Check if domain is valid
    if (yDomain.length === 2 && typeof yDomain[0] === 'number' && typeof yDomain[1] === 'number') {
      const [min, max] = yDomain;

      // If 0 is within the Y-axis domain [min, max], the X-axis is drawn at y=0
      // In this case, we want to show the axis line
      // If 0 is outside the domain, the X-axis is drawn at the edge, so hide the line
      const isZeroInDomain = min <= 0 && max >= 0;
      return !isZeroInDomain;
    }
  }

  return false; // Default to showing the line
}

/**
 * Creates a tick formatter function based on data type and custom formatter.
 * Handles time, linear, and band (string) data types with appropriate defaults.
 *
 * @param xDataType - The type of data on the X-axis ('time', 'linear', or 'band')
 * @param customFormatter - Optional custom formatter function
 * @returns A function that formats tick values for display
 */
export function createTickFormatter(xDataType: XScaleType, customFormatter?: (value: number) => string): TickFormatter {
  if (customFormatter) {
    // Wrap user-provided formatter to handle both string and number inputs
    return (value: string | number) => {
      const numValue = typeof value === 'string' ? Number(value) : value;
      return customFormatter(numValue);
    };
  }

  // Time format: display values as dates
  if (xDataType === 'time') {
    return (value: string | number) => {
      const numValue = toTimeTickNumber(value);
      return Number.isNaN(numValue) ? String(value) : formatDateShort(numValue);
    };
  }

  // Linear format: display as formatted numbers
  if (xDataType === 'linear') {
    return (value: string | number) => {
      const numValue = typeof value === 'string' ? Number(value) : value;
      return typeof numValue === 'number' && !Number.isNaN(numValue)
        ? numValue.toLocaleString('en-US', { maximumFractionDigits: 0 })
        : String(value);
    };
  }

  // Band format: display as strings
  return (value: string | number) => String(value);
}

/**
 * Creates a scale wrapper function that converts values to pixel positions.
 * Normalizes the visx scale to always return number | undefined.
 *
 * @param xScale - The X-axis scale from visx DataContext
 * @returns A function that converts scale values to pixel positions
 */
export function createScaleFunction(xScale: AxisScale | undefined): ScaleFunction | undefined {
  if (!xScale) {
    return undefined;
  }

  return (value: string | number): number | undefined => {
    const result = xScale(value as never);
    return typeof result === 'number' ? result : undefined;
  };
}

/**
 * Calculates the upper bound for tick count based on available width and constraints.
 *
 * @param innerWidth - The chart's inner width
 * @param maxLabelLength - Length of the longest formatted label
 * @param maxNumTicks - Optional maximum tick count constraint
 * @returns The upper bound for tick count (minimum 2)
 */
export function calculateTickUpperBound(
  innerWidth: number | undefined,
  maxLabelLength: number,
  maxNumTicks?: number,
): number {
  const estimatedMax = estimateMaxTicks(innerWidth, maxLabelLength);
  const effectiveMax = Number.isFinite(estimatedMax) ? estimatedMax : 2;

  if (maxNumTicks !== undefined) {
    return Math.min(maxNumTicks, Math.max(effectiveMax, 2));
  }

  return Math.max(effectiveMax, 2);
}

/**
 * Computes tick values for band scales (used in ColumnCharts).
 *
 * @param domain - The band scale domain (array of string values)
 * @param maxNumTicks - Optional maximum number of ticks
 * @param innerWidth - The chart's inner width
 * @param scaleFunction - Function to convert values to pixel positions
 * @param tickFormatter - Function to format tick values
 * @returns Array of tick values, or undefined if calculation fails
 */
export function computeBandScaleTickValues(
  domain: string[],
  maxNumTicks: number | undefined,
  innerWidth: number | undefined,
  scaleFunction: ScaleFunction,
  tickFormatter: TickFormatter,
): Array<string | number> | undefined {
  if (domain.length === 0) {
    return undefined;
  }

  const maxLabelLength = findMaxLabelLength(domain, tickFormatter);
  const upperBound = calculateTickUpperBound(innerWidth, maxLabelLength, maxNumTicks);

  return selectNonOverlappingTicks(domain, upperBound, scaleFunction, tickFormatter);
}

/**
 * Computes tick values for continuous scales (used in LineCharts).
 *
 * @param scaleDomain - The continuous scale domain
 * @param visibleData - The currently visible chart data (filtered by zoom)
 * @param dataKey - The key to extract X values from data points
 * @param maxNumTicks - Optional maximum number of ticks
 * @param innerWidth - The chart's inner width
 * @param scaleFunction - Function to convert values to pixel positions
 * @param tickFormatter - Function to format tick values
 * @returns Array of tick values, or undefined if calculation fails
 */
export function computeContinuousScaleTickValues(
  scaleDomain: [number | Date, number | Date],
  visibleData: ChartDataPoint[] | null | undefined,
  dataKey: string,
  maxNumTicks: number | undefined,
  innerWidth: number | undefined,
  scaleFunction: ScaleFunction,
  tickFormatter: TickFormatter,
): Array<string | number> | undefined {
  const bounds = extractDomainBounds(scaleDomain);
  if (!bounds) {
    return undefined;
  }

  // Get actual unique x-axis values from visible data within the current domain bounds
  const uniqueXValues = getUniqueXValues(visibleData, dataKey, bounds);
  if (uniqueXValues.length === 0) {
    return undefined;
  }

  // Deduplicate adjacent tick labels to prevent duplicates
  // (e.g., multiple timestamps on same day all showing "Jan 1")
  const deduplicatedValues = deduplicateAdjacentTickLabels(uniqueXValues, tickFormatter);

  const maxLabelLength = findMaxLabelLength(deduplicatedValues, tickFormatter);
  const upperBound = calculateTickUpperBound(innerWidth, maxLabelLength, maxNumTicks);

  // Select ticks with overlap detection - will reduce count if labels overlap in pixel space
  // Uses pixel-space selection to handle irregular intervals correctly
  return selectNonOverlappingTicks(deduplicatedValues, upperBound, scaleFunction, tickFormatter);
}

/**
 * Computes numTicks for the evenly-spaced tick strategy.
 * This is used when we want visx to generate nice round tick values.
 *
 * @param xDataType - The type of data on the X-axis ('time', 'linear', or 'band')
 * @param maxNumTicks - Optional explicit maximum tick count
 * @param innerWidth - The chart's inner width
 * @param xScale - The X-axis scale (used as fallback for width calculation)
 * @returns Number of ticks to generate, or undefined to let visx decide
 */
export function computeNumTicksForEvenSpacing(
  xDataType: XScaleType,
  maxNumTicks: number | undefined,
  innerWidth: number | undefined,
  xScale: AxisScale | undefined,
): number | undefined {
  // If maxNumTicks is explicitly set, use it
  if (maxNumTicks !== undefined) {
    return maxNumTicks;
  }

  // Estimate label length in CHARACTERS (not pixels)
  // Time labels like "Jan 28, 8:00 PM" are ~15 chars, linear numbers ~7 chars
  const estimatedLabelChars = xDataType === 'time' ? 12 : 7;

  // Try to use innerWidth if available
  if (innerWidth !== undefined) {
    const estimatedMax = estimateMaxTicks(innerWidth, estimatedLabelChars);
    return Math.max(Number.isFinite(estimatedMax) ? estimatedMax : 4, 2);
  }

  // Fallback: try to get width from scale range
  const availableWidth = getWidthFromScaleRange(xScale);
  if (availableWidth !== undefined && availableWidth > 0) {
    const estimatedMax = estimateMaxTicks(availableWidth, estimatedLabelChars);
    return Math.max(Number.isFinite(estimatedMax) ? estimatedMax : 5, 2);
  }

  // Last resort: conservative default
  return 5;
}

/**
 * Extracts available width from a scale's range.
 *
 * @param scale - The scale to extract width from
 * @returns The width in pixels, or undefined if unavailable
 */
function getWidthFromScaleRange(scale: AxisScale | undefined): number | undefined {
  if (!scale || !('range' in scale) || typeof scale.range !== 'function') {
    return undefined;
  }

  const range = scale.range() as number[];
  if (Array.isArray(range) && range.length >= 2) {
    return Math.abs(range[1] - range[0]);
  }

  return undefined;
}
